import React, { useEffect, useMemo, useRef, useState } from 'react';
import useAxios from '../api/axios';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { format as formatDate } from 'date-fns';
import { fi } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomEditor from '../components/CustomEditor';
import CompetitionCoursePickerDialog from '../components/CompetitionCoursePickerDialog';
import CompetitionCoursesSection from '../components/CompetitionCoursesSection';
import CompetitionFinanceTab from '../components/CompetitionFinanceTab';
import CompetitionOrganizerTab from '../components/CompetitionOrganizerTab';
import CourseDialog from '../components/CourseDialog';
import useAuth from '../hooks/useAuth';
import { normalizeCompetitionOrganizerUserIDs, resolveDefaultCompetitionOrganizerUserIDs } from '../utils/competitionOrganizerUtils';
import { createEmptyCourseForm, normalizeCourseList } from '../utils/courseUtils';
import { MUNICIPALITY_OPTIONS, REGION_OPTIONS } from '../utils/courseLocationData';
import {
  buildPrizeSettingsPayload,
  createDefaultPrizeRule,
  createDefaultPrizeSettings,
  formatMoney,
  isPayoutPrizeType,
  normalizePrizeSettingsForUi,
  parseMoneyValue,
  prizeTypeOptions,
  validatePrizeSettings,
} from '../utils/competitionFinance';

const EMPTY_CLASS_FORM = {
  name: '',
  gender: 'all',
  ageOperator: 'none',
  ageYear: '',
};
 
const tabOptions = [
  { id: 'organizer', label: 'Järjestäjä' },
  { id: 'details', label: 'Perustiedot' },
  { id: 'finance', label: 'Maksut ja palkinnot' },
];

const genderLabels = {
  male: 'Miehet',
  female: 'Naiset',
  all: 'Molemmat',
};

const prizeTypeLabels = prizeTypeOptions.reduce((accumulator, option) => ({
  ...accumulator,
  [option.value]: option.label,
}), {});

export const AddCompetition = () => {
  const navigate = useNavigate();
  const { isAuthenticated, auth } = useAuth();
  const api = useAxios();
  const initializedOrganizerRef = useRef(false);

  const [activeTab, setActiveTab] = useState('organizer');
  const [name, setName] = useState('');
  const [roundtime, setRoundTime] = useState(14);
  const [info, setInfo] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [registrationEndDate, setRegistrationEndDate] = useState(null);
  const [classes, setClasses] = useState([]);
  const [classDialogOpen, setClassDialogOpen] = useState(false);
  const [classForm, setClassForm] = useState(EMPTY_CLASS_FORM);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [canCreateCompetition, setCanCreateCompetition] = useState(false);
  const [organizers, setOrganizers] = useState([]);
  const [organizerID, setOrganizerID] = useState('');
  const [competitionOrganizerUserIDs, setCompetitionOrganizerUserIDs] = useState([]);
  const [useClassSpecificEntryFee, setUseClassSpecificEntryFee] = useState(false);
  const [entryFee, setEntryFee] = useState('');
  const [enableBankPayment, setEnableBankPayment] = useState(false);
  const [bankAccount, setBankAccount] = useState('');
  const [bankRecipientName, setBankRecipientName] = useState('');
  const [enableMobilepayPayment, setEnableMobilepayPayment] = useState(false);
  const [mobilepayNumber, setMobilepayNumber] = useState('');
  const [bankPaymentMessage, setBankPaymentMessage] = useState('');
  const [bankPaymentBarcode, setBankPaymentBarcode] = useState('');
  const [usePersonalReferenceNumber, setUsePersonalReferenceNumber] = useState(false);
  const [mobilepayMessage, setMobilepayMessage] = useState('');
  const [prizeSettings, setPrizeSettings] = useState(createDefaultPrizeSettings());
  const [availableCourses, setAvailableCourses] = useState([]);
  const [selectedCourseIDs, setSelectedCourseIDs] = useState([]);
  const [coursePickerOpen, setCoursePickerOpen] = useState(false);
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [courseForm, setCourseForm] = useState(createEmptyCourseForm());
  const [courseSaving, setCourseSaving] = useState(false);

  const fetchCourses = async () => {
    const response = await api.post(
      '/getCourses.php',
      JSON.stringify({}),
      { headers: { 'Content-Type': 'application/json' } }
    );

    const nextCourses = normalizeCourseList(response?.data?.courses);
    setAvailableCourses(nextCourses);
    return nextCourses;
  };

  useEffect(() => {
    if (!isAuthenticated()) {
      toast.error('Kirjaudu sisään lisätäksesi kilpailun.');
      navigate('/login');
      return;
    }

    let isMounted = true;

    const loadCreatePermission = async () => {
      try {
        setPermissionLoading(true);

        const [organizersResponse] = await Promise.all([
          api.post(
            '/getOrganizers.php',
            JSON.stringify({}),
            { headers: { 'Content-Type': 'application/json' } }
          ),
          fetchCourses(),
        ]);

        if (!isMounted) {
          return;
        }

        const allOrganizers = Array.isArray(organizersResponse?.data?.organizers) ? organizersResponse.data.organizers : [];
        const availableOrganizers = allOrganizers.filter((organizer) => organizer?.permissions?.canCreateCompetitions);
        const hasPermission = availableOrganizers.length > 0;

        setOrganizers(availableOrganizers);
        setCanCreateCompetition(hasPermission);

        if (!hasPermission) {
          toast.error('Kilpailun lisääminen vaatii järjestäjäroolin Hallinta tai Kilpailun järjestäjä.');
          navigate('/kilpailut');
          return;
        }

        const firstOrganizer = availableOrganizers[0];

        if (availableOrganizers.length === 1 && firstOrganizer && !initializedOrganizerRef.current) {
          initializedOrganizerRef.current = true;
          setOrganizerID(String(firstOrganizer.organizerID));
          setCompetitionOrganizerUserIDs(resolveDefaultCompetitionOrganizerUserIDs(firstOrganizer, auth));
          setEnableBankPayment(Boolean(firstOrganizer.enableBankPayment));
          setBankAccount(firstOrganizer.bankAccount || '');
          setBankRecipientName(firstOrganizer.bankRecipientName || firstOrganizer.name || '');
          setEnableMobilepayPayment(Boolean(firstOrganizer.enableMobilepayPayment));
          setMobilepayNumber(firstOrganizer.mobilepayNumber || '');
          setBankPaymentMessage(firstOrganizer.bankPaymentMessage || '');
          setBankPaymentBarcode(firstOrganizer.bankPaymentBarcode || '');
          setUsePersonalReferenceNumber(Boolean(firstOrganizer.usePersonalReferenceNumber));
          setMobilepayMessage(firstOrganizer.mobilepayMessage || '');
        } else if (!initializedOrganizerRef.current) {
          initializedOrganizerRef.current = true;
          setOrganizerID('');
          setCompetitionOrganizerUserIDs([]);
          setEnableBankPayment(false);
          setBankAccount('');
          setBankRecipientName('');
          setEnableMobilepayPayment(false);
          setMobilepayNumber('');
          setBankPaymentMessage('');
          setBankPaymentBarcode('');
          setUsePersonalReferenceNumber(false);
          setMobilepayMessage('');
        }
      } catch {
        if (!isMounted) {
          return;
        }

        setCanCreateCompetition(false);
        setOrganizers([]);
        setAvailableCourses([]);
        toast.error('Kilpailun luontioikeuksien tarkistus epäonnistui.');
        navigate('/kilpailut');
      } finally {
        if (isMounted) {
          setPermissionLoading(false);
        }
      }
    };

    loadCreatePermission();

    return () => {
      isMounted = false;
    };
  // useAxios may return a new instance between renders; initialize organizer defaults only once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.accessToken, isAuthenticated, navigate]);

  const selectedOrganizer = useMemo(
    () => organizers.find((organizer) => String(organizer.organizerID) === String(organizerID)) || null,
    [organizerID, organizers]
  );

  const isOrganizerSelected = Boolean(organizerID);
  const selectedCourses = useMemo(
    () => availableCourses.filter((course) => selectedCourseIDs.includes(Number(course.courseID))),
    [availableCourses, selectedCourseIDs]
  );

  const paymentSummary = useMemo(() => {
    if (!useClassSpecificEntryFee) {
      const parsedEntryFee = parseMoneyValue(entryFee);
      return parsedEntryFee !== null ? formatMoney(parsedEntryFee) : 'Ei asetettu';
    }

    const configuredClassCount = classes.filter((competitionClass) => parseMoneyValue(competitionClass.entryFee) !== null).length;
    return classes.length
      ? `${configuredClassCount}/${classes.length} luokkaa hinnoiteltu`
      : 'Ei luokkia';
  }, [classes, entryFee, useClassSpecificEntryFee]);

  function formatAgeRule(ageOperator, ageYear) {
    if (ageOperator === 'lt' && ageYear) {
      return `Syntymävuosi enintään ${ageYear}`;
    }

    if (ageOperator === 'gt' && ageYear) {
      return `Syntymävuosi vähintään ${ageYear}`;
    }

    return 'Ei ikärajaa';
  }

  function formatDateTimeLocal(date) {
    if (!date) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  function formatStartDateLocal(date) {
    if (!date) return '';

    const nextDate = date instanceof Date ? new Date(date) : new Date(date);

    if (Number.isNaN(nextDate.getTime())) {
      return '';
    }

    nextDate.setHours(0, 0, 0, 0);
    return formatDateTimeLocal(nextDate);
  }

  function formatDateTimeDisplay(date) {
    if (!date) {
      return '';
    }

    const nextDate = date instanceof Date ? date : new Date(date);

    if (Number.isNaN(nextDate.getTime())) {
      return '';
    }

    return formatDate(nextDate, 'dd.MM.yyyy HH:mm', { locale: fi });
  }

  function formatDateDisplay(date) {
    if (!date) {
      return '';
    }

    const nextDate = date instanceof Date ? date : new Date(date);

    if (Number.isNaN(nextDate.getTime())) {
      return '';
    }

    return formatDate(nextDate, 'dd.MM.yyyy', { locale: fi });
  }

  function getTomorrowDateTimeLocal() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow.toISOString().slice(0, 16);
  }

  function infoOnChange(event) {
    setInfo(event.target.value);
  }

  function openClassDialog() {
    setClassForm(EMPTY_CLASS_FORM);
    setClassDialogOpen(true);
  }

  function closeClassDialog() {
    setClassDialogOpen(false);
    setClassForm(EMPTY_CLASS_FORM);
  }

  function handleClassFieldChange(field) {
    return (event) => {
      const nextValue = event.target.value;

      setClassForm((prev) => ({
        ...prev,
        [field]: nextValue,
        ...(field === 'ageOperator' && nextValue === 'none' ? { ageYear: '' } : {}),
      }));
    };
  }

  function saveClass() {
    if (!classForm.name.trim()) {
      toast.error('Anna luokalle nimi');
      return;
    }

    if (classForm.ageOperator !== 'none' && !classForm.ageYear) {
      toast.error('Anna ikäluokalle syntymävuosi');
      return;
    }

    setClasses((prev) => ([
      ...prev,
      {
        id: Date.now(),
        name: classForm.name.trim(),
        gender: classForm.gender,
        ageOperator: classForm.ageOperator,
        ageYear: classForm.ageYear ? Number(classForm.ageYear) : null,
        entryFee: '',
      },
    ]));

    closeClassDialog();
  }

  function removeClass(classId) {
    setClasses((prev) => prev.filter((competitionClass) => competitionClass.id !== classId));
  }

  function handleOrganizerChange(nextOrganizerID) {
    setOrganizerID(String(nextOrganizerID));

    const organizer = organizers.find((item) => String(item.organizerID) === String(nextOrganizerID));
    setCompetitionOrganizerUserIDs(resolveDefaultCompetitionOrganizerUserIDs(organizer, auth));
    setEnableBankPayment(Boolean(organizer?.enableBankPayment));
    setBankAccount(organizer?.bankAccount || '');
    setBankRecipientName(organizer?.bankRecipientName || organizer?.name || '');
    setEnableMobilepayPayment(Boolean(organizer?.enableMobilepayPayment));
    setMobilepayNumber(organizer?.mobilepayNumber || '');
    setBankPaymentMessage(organizer?.bankPaymentMessage || '');
    setBankPaymentBarcode(organizer?.bankPaymentBarcode || '');
    setUsePersonalReferenceNumber(Boolean(organizer?.usePersonalReferenceNumber));
    setMobilepayMessage(organizer?.mobilepayMessage || '');
  }

  function handleCompetitionOrganizerUserToggle(organizerUserID) {
    setCompetitionOrganizerUserIDs((prev) => (
      prev.includes(organizerUserID)
        ? prev.filter((value) => value !== organizerUserID)
        : normalizeCompetitionOrganizerUserIDs([...prev, organizerUserID])
    ));
  }

  function handleClassEntryFeeChange(classId, value) {
    setClasses((prev) => prev.map((competitionClass) => (
      competitionClass.id === classId
        ? { ...competitionClass, entryFee: value }
        : competitionClass
    )));
  }

  function handlePrizeSettingChange(field, value) {
    setPrizeSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function handleAddPrizeRule() {
    setPrizeSettings((prev) => ({
      ...prev,
      rules: [...prev.rules, createDefaultPrizeRule()],
    }));
  }

  function handleRemovePrizeRule(ruleId) {
    setPrizeSettings((prev) => ({
      ...prev,
      rules: prev.rules.filter((rule) => rule.id !== ruleId),
    }));
  }

  function handlePrizeRuleChange(ruleId, field, value, percentageIndex = null) {
    setPrizeSettings((prev) => ({
      ...prev,
      rules: prev.rules.map((rule) => {
        if (rule.id !== ruleId) {
          return rule;
        }

        if (field === 'minPlayers') {
          const nextRule = {
            ...rule,
            minPlayers: value,
          };
          return normalizePrizeSettingsForUi({ rules: [nextRule] }).rules[0];
        }

        if (field === 'winnerCount') {
          const nextRule = {
            ...rule,
            winnerCount: value,
          };
          return normalizePrizeSettingsForUi({ rules: [nextRule] }).rules[0];
        }

        if (field === 'percentage' && percentageIndex !== null) {
          const nextPercentages = [...rule.percentages];
          nextPercentages[percentageIndex] = value;
          return {
            ...rule,
            percentages: nextPercentages,
          };
        }

        return rule;
      }),
    }));
  }

  function toggleCourseSelection(courseID) {
    setSelectedCourseIDs((prev) => (
      prev.includes(courseID)
        ? prev.filter((selectedCourseID) => selectedCourseID !== courseID)
        : [...prev, courseID]
    ));
  }

  function openAddCourseDialog() {
    setCourseForm({
      ...createEmptyCourseForm(),
      municipality: MUNICIPALITY_OPTIONS[0] || '',
      region: REGION_OPTIONS[0] || '',
    });
    setCourseDialogOpen(true);
  }

  function openEditCourseDialog(course) {
    setCourseForm({
      courseID: course.courseID,
      name: course.name || '',
      municipality: course.municipality || '',
      region: course.region || '',
    });
    setCourseDialogOpen(true);
  }

  function closeCourseDialog() {
    if (courseSaving) {
      return;
    }

    setCourseDialogOpen(false);
    setCourseForm(createEmptyCourseForm());
  }

  function openCoursePicker() {
    setCoursePickerOpen(true);
  }

  function closeCoursePicker() {
    setCoursePickerOpen(false);
  }

  function handleCourseFieldChange(field) {
    return (event) => {
      const nextValue = event.target.value;

      setCourseForm((prev) => ({
        ...prev,
        [field]: nextValue,
      }));
    };
  }

  async function saveCourse() {
    const trimmedName = courseForm.name.trim();
    const trimmedMunicipality = courseForm.municipality.trim();
    const trimmedRegion = courseForm.region.trim();

    if (!trimmedName || !trimmedMunicipality || !trimmedRegion) {
      toast.error('Täytä radan nimi, paikkakunta ja maakunta.');
      return;
    }

    try {
      setCourseSaving(true);

      const endpoint = courseForm.courseID ? '/editCourse.php' : '/addCource.php';
      const response = await api.post(endpoint, JSON.stringify({
        course: {
          courseID: courseForm.courseID,
          name: trimmedName,
          municipality: trimmedMunicipality,
          region: trimmedRegion,
        },
      }), {
        headers: { 'Content-Type': 'application/json' },
      });

      const savedCourseID = Number(response?.data?.course?.courseID ?? courseForm.courseID ?? 0);
      await fetchCourses();

      if (savedCourseID > 0) {
        setSelectedCourseIDs((prev) => (prev.includes(savedCourseID) ? prev : [...prev, savedCourseID]));
      }

      toast.success(courseForm.courseID ? 'Rata päivitetty.' : 'Rata lisätty.');
      setCoursePickerOpen(true);
      closeCourseDialog();
    } catch (err) {
      const message = err?.response?.data?.msg || err?.response?.data?.message || err?.message;
      toast.error(message || 'Radan tallennus epäonnistui');
    } finally {
      setCourseSaving(false);
    }
  }

  function validateForm() {
    let ok = true;

    if (name.trim().length === 0) {
      toast.error('Anna kisalle nimi');
      ok = false;
    }

    if (Number(roundtime) <= 0) {
      toast.error('Anna kierroksen kestoksi positiivinen luku');
      ok = false;
    }

    if (!startDate) {
      toast.error('Valitse kisan alkamisaika');
      ok = false;
    }

    if (!registrationEndDate) {
      toast.error('Valitse rekisteröinnin päättymisaika');
      ok = false;
    }

    if (startDate && registrationEndDate && new Date(registrationEndDate) > new Date(startDate)) {
      toast.error('Rekisteröinnin päättymisajan tulee olla ennen kisan alkua');
      ok = false;
    }

    if (!organizerID) {
      toast.error('Valitse kilpailulle järjestäjä');
      ok = false;
    }

    if (competitionOrganizerUserIDs.length === 0) {
      toast.error('Valitse kilpailulle vähintään yksi ylläpitäjä');
      setActiveTab('organizer');
      ok = false;
    }

    if (!useClassSpecificEntryFee) {
      const parsedEntryFee = parseMoneyValue(entryFee);
      if (entryFee !== '' && (parsedEntryFee === null || parsedEntryFee < 0)) {
        toast.error('Anna kelvollinen osallistumismaksu');
        ok = false;
      }
    } else {
      if (!classes.length) {
        toast.error('Lisää vähintään yksi luokka luokkakohtaista maksua varten');
        ok = false;
      }

      const missingClassFee = classes.some((competitionClass) => {
        const parsedEntryFee = parseMoneyValue(competitionClass.entryFee);
        return parsedEntryFee === null || parsedEntryFee < 0;
      });

      if (missingClassFee) {
        toast.error('Aseta kaikille luokille osallistumismaksu');
        ok = false;
      }
    }

    const prizeValidationMessage = validatePrizeSettings(prizeSettings);
    if (prizeValidationMessage) {
      toast.error(prizeValidationMessage);
      ok = false;
    }

    return ok;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canCreateCompetition) {
      toast.error('Sinulla ei ole oikeutta lisätä kilpailua.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      const formattedStartDate = formatStartDateLocal(startDate);
      const formattedRegistrationEndDate = formatDateTimeLocal(registrationEndDate);
      const payloadClasses = classes.map((competitionClass) => ({
        name: competitionClass.name,
        gender: competitionClass.gender,
        ageOperator: competitionClass.ageOperator,
        ageYear: competitionClass.ageYear,
        entryFee: useClassSpecificEntryFee ? parseMoneyValue(competitionClass.entryFee) : null,
      }));

      const params = JSON.stringify({
        competition: {
          organizerID: Number(organizerID),
          competitionOrganizerUserIDs: competitionOrganizerUserIDs.map(Number),
          name,
          roundtime: Number(roundtime),
          startDate: formattedStartDate,
          registrationEndDate: formattedRegistrationEndDate,
          info,
          useClassSpecificEntryFee,
          entryFee: useClassSpecificEntryFee ? null : parseMoneyValue(entryFee),
          enableBankPayment,
          bankAccount: bankAccount.trim(),
          bankRecipientName: bankRecipientName.trim(),
          enableMobilepayPayment,
          mobilepayNumber: mobilepayNumber.trim(),
          bankPaymentMessage: usePersonalReferenceNumber ? '' : bankPaymentMessage.trim(),
          bankPaymentBarcode: usePersonalReferenceNumber ? '' : bankPaymentBarcode.trim(),
          usePersonalReferenceNumber,
          mobilepayMessage: usePersonalReferenceNumber ? '' : mobilepayMessage.trim(),
          prizeSettings: buildPrizeSettingsPayload(prizeSettings),
          courseIDs: selectedCourseIDs.map(Number),
          classes: payloadClasses,
        },
      });

      await api.post('/addCompetition', params, {
        headers: { 'Content-Type': 'application/json' },
      });

      toast.info('Kisa tiedot lisätty');
      navigate(-1);
    } catch (err) {
      const message = err?.response?.data?.msg || err?.response?.data?.message || err?.message;
      toast.error(message || 'Kisan lisäys epäonnistui');
    }
  };

  const startPreview = formatDateDisplay(startDate);
  const registrationPreview = formatDateTimeDisplay(registrationEndDate);

  if (permissionLoading) {
    return (
      <div className="mx-auto max-w-6xl px-4 pb-12 pt-24">
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-10 text-center text-slate-300">
          <p className="text-lg font-semibold text-white">Tarkistetaan oikeuksia...</p>
        </div>
      </div>
    );
  }

  if (!canCreateCompetition) {
    return null;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 pb-12 pt-24">
      <div className="mb-6 rounded-[28px] border border-white/10 bg-slate-900/70 p-6 shadow-2xl shadow-slate-950/30 backdrop-blur md:flex md:items-start md:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">Hallinta</p>
          <h1 className="mt-2 text-3xl font-black text-white md:text-4xl">Lisää kilpailu</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 md:text-base">
            Luo uusi kilpailu täyttämällä perustiedot, aikataulu, maksut ja palkintoasetukset.
          </p>
        </div>

        <div className="mt-4 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-3 text-left md:mt-0 md:max-w-xs">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-300">Julkaisuvalmis</p>
          <p className="mt-2 text-sm leading-6 text-slate-200">
            Valitse ensin järjestäjä, koska maksutiedot kopioidaan sieltä kilpailulle muokattavaan muotoon.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.45fr)_minmax(280px,0.75fr)]">
            <div className="rounded-[28px] border border-white/10 bg-slate-900/70 p-5 shadow-2xl shadow-slate-950/30 backdrop-blur">
              <div className="mb-6 flex flex-wrap gap-3 border-b border-white/10 pb-4">
                {tabOptions.map((tab) => (
                  (() => {
                    const isLocked = tab.id !== 'organizer' && !isOrganizerSelected;

                    return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      if (!isLocked) {
                        setActiveTab(tab.id);
                      }
                    }}
                    disabled={isLocked}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                      activeTab === tab.id
                        ? 'bg-cyan-400 text-slate-950'
                        : isLocked
                          ? 'cursor-not-allowed border border-white/10 bg-white/5 text-slate-500 opacity-60'
                          : 'border border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {tab.label}
                  </button>
                    );
                  })()
                ))}
              </div>

              {activeTab === 'organizer' ? (
                <CompetitionOrganizerTab
                  organizers={organizers}
                  organizerID={organizerID}
                  onOrganizerChange={handleOrganizerChange}
                  selectedCompetitionOrganizerUserIDs={competitionOrganizerUserIDs}
                  onCompetitionOrganizerUserToggle={handleCompetitionOrganizerUserToggle}
                />
              ) : activeTab === 'details' ? (
                <>
                  <div className="border-b border-white/10 pb-4">
                    <h2 className="text-xl font-bold text-white">Kilpailun tiedot</h2>
                    <p className="mt-2 text-sm leading-6 text-slate-300">
                      Syötä kilpailun nimi, aikataulu ja kuvaus mahdollisimman selkeästi.
                    </p>
                  </div>

                  <div className="mt-5 grid gap-4 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <TextField
                        variant="filled"
                        required
                        id="name"
                        label="Kilpailun nimi"
                        name="name"
                        autoComplete="off"
                        autoFocus
                        value={name}
                        sx={{ width: 1, backgroundColor: 'white', borderRadius: '12px' }}
                        onChange={(event) => setName(event.target.value)}
                      />
                    </div>

                    <TextField
                      variant="filled"
                      required
                      id="roundtime"
                      label="Kierroksen kesto (päivää)"
                      name="roundtime"
                      type="number"
                      value={roundtime}
                      inputProps={{ min: 1, step: 1 }}
                      sx={{ width: 1, backgroundColor: 'white', borderRadius: '12px' }}
                      onChange={(event) => setRoundTime(event.target.value)}
                    />

                    <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-left text-sm text-slate-300">
                      <p className="font-semibold text-white">Pakolliset tiedot</p>
                      <p className="mt-2 leading-6">
                        Nimi, kisan alkamisaika ja rekisteröinnin päättymisaika tarvitaan ennen tallennusta.
                      </p>
                    </div>

                    <DatePicker
                      label="Kisa alkaa"
                      format="dd.MM.yyyy"
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      minDate={new Date(getTomorrowDateTimeLocal())}
                      slotProps={{
                        textField: {
                          required: true,
                          fullWidth: true,
                          variant: 'filled',
                          sx: { backgroundColor: 'white', borderRadius: '12px' },
                        },
                      }}
                    />

                    <DateTimePicker
                      label="Rekisteröinnin päättymisaika"
                      ampm={false}
                      format="dd.MM.yyyy HH:mm"
                      value={registrationEndDate}
                      onChange={(newValue) => setRegistrationEndDate(newValue)}
                      minDateTime={new Date(getTomorrowDateTimeLocal())}
                      slotProps={{
                        textField: {
                          required: true,
                          fullWidth: true,
                          variant: 'filled',
                          sx: { backgroundColor: 'white', borderRadius: '12px' },
                        },
                      }}
                    />
                  </div>

                  <div className="mt-6">
                    <div className="mb-3 text-left">
                      <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-300">Lisätiedot</p>
                      <p className="mt-2 text-sm leading-6 text-slate-300">
                        Lisää tähän kilpailun kuvaus, mahdolliset ohjeet ja muu osallistujille olennainen tieto.
                      </p>
                    </div>

                    <CustomEditor
                      value={info}
                      onChange={infoOnChange}
                      containerProps={{
                        className: 'mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white text-left text-black shadow-inner',
                        style: { color: '#000', minHeight: '260px' },
                      }}
                    />
                  </div>

                  <CompetitionCoursesSection
                    selectedCourses={selectedCourses}
                    onOpenPicker={openCoursePicker}
                  />

                  <div className="mt-6 rounded-[24px] border border-white/10 bg-white/5 p-4">
                    <div className="flex flex-col gap-3 border-b border-white/10 pb-4 md:flex-row md:items-center md:justify-between">
                      <div className="text-left">
                        <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-300">Luokat</p>
                        <p className="mt-2 text-sm leading-6 text-slate-300">
                          Lisää kilpailuun kuuluvat luokat. Jokaiselle luokalle voi määrittää nimen, sukupuolen ja valinnaisen ikärajan.
                        </p>
                      </div>
                      <Button type="button" variant="contained" color="primary" onClick={openClassDialog}>Lisää luokka</Button>
                    </div>

                    <div className="mt-4 overflow-hidden rounded-2xl border border-white/10">
                      <table className="min-w-full divide-y divide-white/10 text-left text-sm text-slate-200">
                        <thead className="bg-white/10 text-xs uppercase tracking-[0.2em] text-cyan-300">
                          <tr>
                            <th className="px-4 py-3 font-semibold">Luokka</th>
                            <th className="px-4 py-3 font-semibold">Sukupuoli</th>
                            <th className="px-4 py-3 font-semibold">Ikäraja</th>
                            <th className="px-4 py-3 font-semibold">Toiminnot</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/10 bg-slate-950/20">
                          {classes.length ? classes.map((competitionClass) => (
                            <tr key={competitionClass.id}>
                              <td className="px-4 py-3 font-semibold text-white">{competitionClass.name}</td>
                              <td className="px-4 py-3">{genderLabels[competitionClass.gender]}</td>
                              <td className="px-4 py-3">{formatAgeRule(competitionClass.ageOperator, competitionClass.ageYear)}</td>
                              <td className="px-4 py-3">
                                <Button type="button" variant="contained" color="error" onClick={() => removeClass(competitionClass.id)}>Poista</Button>
                              </td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan="4" className="px-4 py-6 text-center text-slate-400">
                                Luokkia ei ole vielä lisätty.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : (
                <CompetitionFinanceTab
                  key={`finance-${organizerID || 'none'}`}
                  useClassSpecificEntryFee={useClassSpecificEntryFee}
                  onFeeModeChange={setUseClassSpecificEntryFee}
                  entryFee={entryFee}
                  onEntryFeeChange={setEntryFee}
                  classes={classes}
                  onClassEntryFeeChange={handleClassEntryFeeChange}
                  enableBankPayment={enableBankPayment}
                  onEnableBankPaymentChange={setEnableBankPayment}
                  bankAccount={bankAccount}
                  onBankAccountChange={setBankAccount}
                  bankRecipientName={bankRecipientName}
                  onBankRecipientNameChange={setBankRecipientName}
                  enableMobilepayPayment={enableMobilepayPayment}
                  onEnableMobilepayPaymentChange={setEnableMobilepayPayment}
                  mobilepayNumber={mobilepayNumber}
                  onMobilepayNumberChange={setMobilepayNumber}
                  bankPaymentMessage={bankPaymentMessage}
                  onBankPaymentMessageChange={setBankPaymentMessage}
                  bankPaymentBarcode={bankPaymentBarcode}
                  onBankPaymentBarcodeChange={setBankPaymentBarcode}
                  usePersonalReferenceNumber={usePersonalReferenceNumber}
                  onUsePersonalReferenceNumberChange={setUsePersonalReferenceNumber}
                  mobilepayMessage={mobilepayMessage}
                  onMobilepayMessageChange={setMobilepayMessage}
                  prizeSettings={prizeSettings}
                  onPrizeSettingChange={handlePrizeSettingChange}
                  onPrizeRuleChange={handlePrizeRuleChange}
                  onAddPrizeRule={handleAddPrizeRule}
                  onRemovePrizeRule={handleRemovePrizeRule}
                />
              )}

              <Stack direction="row" alignItems="center" justifyContent="flex-start" sx={{ pt: 3 }} spacing={2}>
                <Button type="submit" variant="contained" color="primary">Tallenna kilpailu</Button>
                <Button variant="contained" color="secondary" onClick={() => navigate(-1)}>Peruuta</Button>
              </Stack>
            </div>

            <div className="space-y-6">
              <div className="rounded-[28px] border border-white/10 bg-slate-900/70 p-5 shadow-2xl shadow-slate-950/30 backdrop-blur">
                <h2 className="text-xl font-bold text-white">Yhteenveto</h2>
                <div className="mt-4 space-y-3 text-sm text-slate-300">
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.25em] text-cyan-300">Kilpailu</p>
                    <p className="mt-2 text-base font-semibold text-white">{name.trim() || 'Anna kilpailulle nimi'}</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.25em] text-cyan-300">Järjestäjä</p>
                    <p className="mt-2 text-base font-semibold text-white">{selectedOrganizer?.name || 'Ei valittu'}</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.25em] text-cyan-300">Kierroksen kesto</p>
                    <p className="mt-2 text-base font-semibold text-white">{roundtime ? `${roundtime} päivää` : 'Ei asetettu'}</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.25em] text-cyan-300">Kisa alkaa</p>
                    <p className="mt-2 text-base font-semibold text-white">{startPreview || 'Ei asetettu'}</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.25em] text-cyan-300">Rekisteröinti päättyy</p>
                    <p className="mt-2 text-base font-semibold text-white">{registrationPreview || 'Ei asetettu'}</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.25em] text-cyan-300">Luokkia</p>
                    <p className="mt-2 text-base font-semibold text-white">{classes.length ? `${classes.length} kpl` : 'Ei lisätty'}</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.25em] text-cyan-300">Radat</p>
                    <p className="mt-2 text-base font-semibold text-white">{selectedCourseIDs.length ? `${selectedCourseIDs.length} valittu` : 'Ei valittu'}</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.25em] text-cyan-300">Osallistumismaksut</p>
                    <p className="mt-2 text-base font-semibold text-white">{paymentSummary}</p>
                    <p className="mt-1 text-xs text-slate-400">{useClassSpecificEntryFee ? 'Luokkakohtainen' : 'Yhteinen maksu'}</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.25em] text-cyan-300">Palkinnot</p>
                    <p className="mt-2 text-base font-semibold text-white">
                      {isPayoutPrizeType(prizeSettings.prizeType)
                        ? `${prizeSettings.payoutAmount || '0'} % · ${prizeTypeLabels[prizeSettings.prizeType] || 'Käteinen'}`
                        : (prizeTypeLabels[prizeSettings.prizeType] || 'Käteinen')}
                    </p>
                    <p className="mt-1 text-xs text-slate-400">
                      {isPayoutPrizeType(prizeSettings.prizeType)
                        ? (prizeSettings.rules.length ? `${prizeSettings.rules.length} sääntöä` : 'Ei sääntöjä')
                        : 'Ei payout-jakoa'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-[28px] border border-white/10 bg-slate-900/70 p-5 shadow-2xl shadow-slate-950/30 backdrop-blur">
                <h2 className="text-xl font-bold text-white">Tarkistuslista</h2>
                <div className="mt-4 space-y-3 text-sm leading-6 text-slate-300">
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="font-semibold text-white">Aikataulu</p>
                    <p className="mt-1">Varmista, että rekisteröinti päättyy ennen kilpailun alkua.</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="font-semibold text-white">Maksut</p>
                    <p className="mt-1">Tarkista, että tilinumero ja MobilePay on kopioitu oikealta järjestäjältä.</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="font-semibold text-white">Palkinnot</p>
                    <p className="mt-1">Syötä prosentit jokaiselle palkintoryhmälle. Yhteissumman tulee olla tasan 100 %.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </LocalizationProvider>
      </form>

      <Dialog open={classDialogOpen} onClose={closeClassDialog} fullWidth maxWidth="sm">
        <DialogTitle>Lisää luokka</DialogTitle>
        <DialogContent>
          <div className="mt-2 grid gap-4">
            <TextField
              label="Luokan nimi"
              variant="filled"
              value={classForm.name}
              onChange={handleClassFieldChange('name')}
              fullWidth
            />

            <FormControl variant="filled" fullWidth>
              <InputLabel id="class-gender-label">Sukupuoli</InputLabel>
              <Select
                labelId="class-gender-label"
                value={classForm.gender}
                onChange={handleClassFieldChange('gender')}
              >
                <MenuItem value="male">Miehet</MenuItem>
                <MenuItem value="female">Naiset</MenuItem>
                <MenuItem value="all">Molemmat</MenuItem>
              </Select>
            </FormControl>

            <FormControl variant="filled" fullWidth>
              <InputLabel id="class-age-operator-label">Ikäraja</InputLabel>
              <Select
                labelId="class-age-operator-label"
                value={classForm.ageOperator}
                onChange={handleClassFieldChange('ageOperator')}
              >
                <MenuItem value="none">Ei ikärajaa</MenuItem>
                <MenuItem value="lt">Syntymävuosi enintään</MenuItem>
                <MenuItem value="gt">Syntymävuosi vähintään</MenuItem>
              </Select>
            </FormControl>

            {classForm.ageOperator !== 'none' ? (
              <TextField
                label="Syntymävuosi"
                variant="filled"
                type="number"
                value={classForm.ageYear}
                onChange={handleClassFieldChange('ageYear')}
                inputProps={{ min: 1900, max: 2100, step: 1 }}
                fullWidth
              />
            ) : null}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeClassDialog} variant="outlined" color="secondary">Peruuta</Button>
          <Button onClick={saveClass} variant="contained" color="primary">Lisää luokka</Button>
        </DialogActions>
      </Dialog>

      <CourseDialog
        open={courseDialogOpen}
        courseForm={courseForm}
        saving={courseSaving}
        onClose={closeCourseDialog}
        onSubmit={saveCourse}
        onFieldChange={handleCourseFieldChange}
      />

      <CompetitionCoursePickerDialog
        open={coursePickerOpen}
        courses={availableCourses}
        selectedCourseIDs={selectedCourseIDs}
        onClose={closeCoursePicker}
        onToggleCourse={toggleCourseSelection}
        onOpenAddCourse={openAddCourseDialog}
        onOpenEditCourse={openEditCourseDialog}
      />
    </div>
  );
};